// The three small reads the security-alerts list needs beside audit_events
// (KAN-661/662): who the viewer is + when they joined this tenant, each
// member's role (an `employee/deactivated` alert only matters for an admin),
// and display names for ids an audit payload carries without a name. Every
// read fails SOFT — a failure never hides an alert; it only skips a
// refinement (join-date floor / name / admin-only gate).
import { supabase } from '../supabase';

/** @returns {Promise<{ userId: string|null, email: string|null }>} local session, no network */
export async function fetchViewerIdentity() {
  try {
    const { data } = await supabase.auth.getSession();
    const user = data?.session?.user;
    return { userId: user?.id || null, email: user?.email || null };
  } catch {
    return { userId: null, email: null };
  }
}

/**
 * Tenant memberships → role map + the viewer's join date
 * (`invite_accepted_at`, falling back to `created_at`).
 * @returns {Promise<{ roleByUserId: Map<string,string>, joinedAt: string|null, error: any }>}
 */
export async function fetchMembershipDirectory(tenantId, viewerId) {
  const roleByUserId = new Map();
  let joinedAt = null;
  try {
    // bounded: one tenant's team (seat-limited, far below the 1000-row cap).
    const { data, error } = await supabase
      .from('tenant_memberships')
      .select('user_id, role, invite_accepted_at, created_at')
      .eq('tenant_id', tenantId);
    if (error || !Array.isArray(data)) return { roleByUserId, joinedAt, error: error || null };
    data.forEach((m) => {
      if (m?.user_id) roleByUserId.set(m.user_id, m.role);
      if (viewerId && m?.user_id === viewerId) joinedAt = m.invite_accepted_at || m.created_at || null;
    });
    return { roleByUserId, joinedAt, error: null };
  } catch (error) {
    return { roleByUserId, joinedAt, error };
  }
}

/** @returns {Promise<Map<string,string>>} userId → full name (only non-empty) */
export async function fetchNamesByUserId(userIds) {
  const names = new Map();
  if (!Array.isArray(userIds) || userIds.length === 0) return names;
  try {
    const { data, error } = await supabase.from('profiles').select('id, full_name').in('id', userIds);
    if (error || !Array.isArray(data)) return names;
    data.forEach((p) => { if (p?.id && p.full_name) names.set(p.id, p.full_name); });
  } catch { /* names are a refinement */ }
  return names;
}
